/**
 * Hook for managing citation state — fetch, cache, loading, errors.
 *
 * SRP: Only manages citation query state. No UI, no text selection logic.
 *      Text selection is handled by services/textSelection.ts.
 * DIP: Depends on the getCitations API contract, not on Asthra internals.
 */

import { useCallback, useRef, useState } from "react";
import { getCitations } from "../api/client";
import type { Citation, CitationResponse } from "../api/client";

export interface UseCitationsParams {
  trial?: string;
  session?: string;
  /** Required. The server maps (section_number, docType) → module_id;
   *  a wrong or defaulted docType silently returns an empty citation
   *  set because the expected module file isn't on disk. Callers
   *  must thread the active run's doc type in explicitly — no
   *  fallback. */
  docType: string;
}

export interface UseCitationsResult {
  citations: Citation[];
  status: CitationResponse["status"] | null;
  message: string | null;
  /** Asthra transaction IDs the server queried — used by the popover
   *  empty state to distinguish "no sources to look up" from "sources
   *  searched, no matches". */
  transactionIdsUsed: string[];
  selectedText: string;
  isLoading: boolean;
  error: string | null;
  /** Fetch citations for the given text + section. */
  fetchCitations: (selectedText: string, sectionNumber: string) => Promise<void>;
  /** Clear citations (e.g. when selection changes). */
  clear: () => void;
}

export function useCitations(params: UseCitationsParams): UseCitationsResult {
  const [citations, setCitations] = useState<Citation[]>([]);
  const [status, setStatus] = useState<CitationResponse["status"] | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [transactionIdsUsed, setTransactionIdsUsed] = useState<string[]>([]);
  const [selectedText, setSelectedText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Client-side cache — avoids re-fetching the same query
  const cacheRef = useRef<Map<string, CitationResponse>>(new Map());
  const latestRequestIdRef = useRef(0);

  const fetchCitations = useCallback(
    async (text: string, sectionNumber: string) => {
      if (!params.trial || !params.session) {
        setError("No run context available. Generate a section first.");
        return;
      }
      if (!text.trim()) {
        setError("No text selected. Select text in the document first.");
        return;
      }

      // Check client-side cache
      const cacheKey = `${text.slice(0, 200)}|${sectionNumber}`;
      const requestId = ++latestRequestIdRef.current;
      const cached = cacheRef.current.get(cacheKey);
      if (cached) {
        if (requestId !== latestRequestIdRef.current) return;
        setCitations(cached.citations);
        setStatus(cached.status);
        setMessage(cached.message);
        setTransactionIdsUsed(cached.transaction_ids_used ?? []);
        setSelectedText(text);
        setError(null);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);
      setSelectedText(text);

      try {
        const result = await getCitations({
          selected_text: text,
          section_number: sectionNumber,
          trial: params.trial,
          session: params.session,
          doc_type: params.docType,
        });

        cacheRef.current.set(cacheKey, result);
        if (requestId !== latestRequestIdRef.current) return;

        setCitations(result.citations);
        setStatus(result.status);
        setMessage(result.message);
        setTransactionIdsUsed(result.transaction_ids_used ?? []);
      } catch (err) {
        if (requestId !== latestRequestIdRef.current) return;
        setError(err instanceof Error ? err.message : String(err));
        setCitations([]);
        setStatus("upstream_error");
        setTransactionIdsUsed([]);
      } finally {
        if (requestId === latestRequestIdRef.current) {
          setIsLoading(false);
        }
      }
    },
    [params.trial, params.session, params.docType],
  );

  const clear = useCallback(() => {
    latestRequestIdRef.current += 1;
    setCitations([]);
    setStatus(null);
    setMessage(null);
    setTransactionIdsUsed([]);
    setSelectedText("");
    setError(null);
    setIsLoading(false);
  }, []);

  return {
    citations,
    status,
    message,
    transactionIdsUsed,
    selectedText,
    isLoading,
    error,
    fetchCitations,
    clear,
  };
}
